import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FinanceBook, FinanceCompany, FinanceDocument, FinanceDocumentType } from '../types';
import { financeBooksAPI, financeBookTypesAPI, financeDocumentsAPI } from '../services/api';

type DocumentFormState = {
  baseNumber: string;
  subNumber: string;
  subject: string;
  documentType: string; // by name (backend stores name)
  dateReceived: Date | null;
  sender: string;
  numberAndDate: string;
  organizationalUnit: string;
  oznaka: string;
  description: string;
};

const formatDate = (value?: string) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleDateString('sr-RS');
  } catch {
    return '-';
  }
};

const FinanceBookDocuments: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [book, setBook] = useState<FinanceBook | null>(null);
  const [documents, setDocuments] = useState<FinanceDocument[]>([]);
  const [docTypes, setDocTypes] = useState<FinanceDocumentType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterDate, setFilterDate] = useState<Date | null>(null);

  const [detailVisible, setDetailVisible] = useState(false);
  const [detailDoc, setDetailDoc] = useState<FinanceDocument | null>(null);

  // Subject-only dialog (production behavior)
  const [subjectVisible, setSubjectVisible] = useState(false);
  const [subjectText, setSubjectText] = useState('');

  // Attachments dialog + preview
  const [attachmentsVisible, setAttachmentsVisible] = useState(false);
  const [attachmentsDoc, setAttachmentsDoc] = useState<FinanceDocument | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewName, setPreviewName] = useState('');

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingDoc, setEditingDoc] = useState<FinanceDocument | null>(null);
  const [formData, setFormData] = useState<DocumentFormState>({
    baseNumber: '',
    subNumber: '',
    subject: '',
    documentType: '',
    dateReceived: new Date(),
    sender: '',
    numberAndDate: '',
    organizationalUnit: '',
    oznaka: '',
    description: ''
  });

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [bookData, typesData] = await Promise.all([
        financeBooksAPI.getById(id),
        financeBookTypesAPI.getAll(), // document types
      ]);
      setBook(bookData);
      setDocTypes(typesData);

      // Pull documents for this book (server-side filters applied below as needed)
      const docs = await financeDocumentsAPI.getAll({ bookId: id });
      setDocuments(docs);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Greška pri učitavanju dokumenata';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const companyLabel = useMemo(() => {
    const c = book?.companyId as any;
    if (!c) return '';
    if (typeof c === 'string') return c;
    const cc = c as FinanceCompany;
    return `${cc.name}`;
  }, [book]);

  const filteredDocs = useMemo(() => {
    const q = search.trim().toLowerCase();
    const type = filterType;
    const date = filterDate ? new Date(filterDate) : null;
    const dayStart = date ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0) : null;
    const dayEnd = date ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59) : null;

    return documents.filter((d) => {
      if (type && (d.documentType || '') !== type) return false;
      if (dayStart && dayEnd) {
        const dt = d.dateReceived ? new Date(d.dateReceived) : new Date(d.date);
        if (!(dt >= dayStart && dt <= dayEnd)) return false;
      }
      if (!q) return true;
      const base = (d.baseNumber || '').toLowerCase();
      const sub = (d.subNumber || '').toLowerCase();
      const subj = (d.subject || '').toLowerCase();
      const sender = (d.sender || '').toLowerCase();
      const ref = (d.numberAndDate || d.referenceNumber || '').toLowerCase();
      return base.includes(q) || sub.includes(q) || subj.includes(q) || sender.includes(q) || ref.includes(q);
    });
  }, [documents, filterDate, filterType, search]);

  const openCreateDialog = () => {
    setEditingDoc(null);
    setFormData({
      baseNumber: '',
      subNumber: '',
      subject: '',
      documentType: '',
      dateReceived: new Date(),
      sender: '',
      numberAndDate: '',
      organizationalUnit: '',
      oznaka: '',
      description: ''
    });
    setPendingFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setError('');
    setDialogVisible(true);
  };

  const openEditDialog = (doc: FinanceDocument) => {
    setEditingDoc(doc);
    setFormData({
      baseNumber: doc.baseNumber || '',
      subNumber: doc.subNumber || '',
      subject: doc.subject || '',
      documentType: doc.documentType || '',
      dateReceived: doc.dateReceived ? new Date(doc.dateReceived) : (doc.date ? new Date(doc.date) : new Date()),
      sender: doc.sender || '',
      numberAndDate: doc.numberAndDate || doc.referenceNumber || '',
      organizationalUnit: doc.organizationalUnit || '',
      oznaka: doc.oznaka || '',
      description: doc.description || ''
    });
    setPendingFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setError('');
    setDialogVisible(true);
  };

  const handleDelete = (doc: FinanceDocument) => {
    confirmDialog({
      message: `Da li ste sigurni da želite da obrišete dokument ${doc.documentNumber}?`,
      header: 'Potvrda brisanja',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Obriši',
      rejectLabel: 'Otkaži',
      accept: async () => {
        try {
          await financeDocumentsAPI.delete(doc._id);
          await load();
        } catch (err: any) {
          const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Greška pri brisanju dokumenta';
          setError(msg);
        }
      }
    });
  };

  const handleSubmit = async () => {
    if (!id) return;
    if (!formData.baseNumber.trim()) return setError('Osnovni broj je obavezan');
    if (!formData.subject.trim()) return setError('Predmet je obavezan');
    if (!formData.dateReceived) return setError('Datum prijema je obavezan');

    try {
      setError('');
      let saved: FinanceDocument;
      const payload = {
        bookId: id,
        baseNumber: formData.baseNumber.trim(),
        subNumber: formData.subNumber.trim() || undefined,
        dateReceived: formData.dateReceived ? formData.dateReceived.toISOString() : undefined,
        sender: formData.sender.trim() || undefined,
        subject: formData.subject.trim(),
        documentType: formData.documentType || undefined,
        numberAndDate: formData.numberAndDate.trim() || undefined,
        organizationalUnit: formData.organizationalUnit.trim() || undefined,
        oznaka: formData.oznaka.trim() || undefined,
        description: formData.description.trim() || undefined,
      };

      if (editingDoc) {
        saved = await financeDocumentsAPI.update(editingDoc._id, payload as any);
      } else {
        saved = await financeDocumentsAPI.create(payload);
      }

      // Upload attachments if selected
      if (pendingFiles.length > 0) {
        await financeDocumentsAPI.uploadAttachments(saved._id, pendingFiles);
      }

      setDialogVisible(false);
      await load();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Greška pri čuvanju dokumenta';
      setError(msg);
    }
  };

  const openDocDetails = (doc: FinanceDocument) => {
    setDetailDoc(doc);
    setDetailVisible(true);
  };

  const openSubjectOnly = (doc: FinanceDocument) => {
    setSubjectText(doc.subject || '-');
    setSubjectVisible(true);
  };

  const openAttachments = (doc: FinanceDocument) => {
    setAttachmentsDoc(doc);
    setAttachmentsVisible(true);
  };

  const openPreview = (doc: FinanceDocument, filename: string) => {
    setPreviewName(filename);
    setPreviewUrl(financeDocumentsAPI.getAttachmentUrl(doc._id, filename));
    setPreviewVisible(true);
  };

  const isImage = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '');
  };

  const isPdf = (filename: string) => filename.toLowerCase().endsWith('.pdf');

  const handlePrintPreview = () => {
    if (!previewUrl) return;
    // Open a clean tab/window for printing (closest UX to production modal print)
    const w = window.open(previewUrl, '_blank', 'noopener,noreferrer');
    w?.focus();
  };

  const baseNumberTemplate = (rowData: FinanceDocument) => {
    const label = rowData.baseNumber || '-';
    return (
      <Button
        label={label}
        className="p-button-link p-button-sm"
        onClick={() => openDocDetails(rowData)}
      />
    );
  };

  const subNumberTemplate = (rowData: FinanceDocument) => {
    return rowData.subNumber || '-';
  };

  const dateTemplate = (rowData: FinanceDocument) => {
    return formatDate(rowData.dateReceived || rowData.date);
  };

  const subjectTemplate = (rowData: FinanceDocument) => (
    <Button
      icon="pi pi-eye"
      className="p-button-text p-button-sm"
      onClick={() => openSubjectOnly(rowData)}
      aria-label="Predmet"
    />
  );

  const attachmentsTemplate = (rowData: FinanceDocument) => {
    const count = rowData.attachments?.length || 0;
    if (count === 0) return '-';
    return (
      <Button
        icon="pi pi-eye"
        label={String(count)}
        className="p-button-text p-button-sm"
        onClick={() => openAttachments(rowData)}
        aria-label="Prilozi"
      />
    );
  };

  const actionsTemplate = (rowData: FinanceDocument) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-pencil"
          className="p-button-text p-button-sm"
          onClick={() => openEditDialog(rowData)}
          aria-label="Izmeni"
        />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-sm p-button-danger"
          onClick={() => handleDelete(rowData)}
          aria-label="Obriši"
        />
      </div>
    );
  };

  const dialogFooter = (
    <div className="flex justify-content-end gap-2">
      <Button label="Otkaži" className="p-button-text" onClick={() => setDialogVisible(false)} />
      <Button label="Kreiraj" className="p-button-success" onClick={handleSubmit} />
    </div>
  );

  return (
    <div className="p-4">
      <ConfirmDialog />

      <div className="mb-3">
        <Button icon="pi pi-arrow-left" label="Nazad na knjige" className="p-button-text" onClick={() => navigate('/finance/books')} />
      </div>

      {book && (
        <div
          style={{
            borderRadius: 10,
            padding: '1rem',
            background: '#fef9c3',
            border: '1px solid #fde68a',
            marginBottom: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '1rem',
            flexWrap: 'wrap'
          }}
        >
          <div className="flex align-items-center gap-3">
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius: 10,
                background: '#3b82f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <i className="pi pi-book" style={{ color: '#fff', fontSize: '1.2rem' }} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 18 }}>{book.name}</div>
              <div style={{ color: '#374151' }}>
                {book.code} • Godina: {book.year} • Dokumenta: {book.documentCount ?? '-'} • Firma: {companyLabel}
              </div>
            </div>
          </div>

          <Button label="Dodaj dokument u ovu knjigu" icon="pi pi-plus" className="p-button-success" onClick={openCreateDialog} />
        </div>
      )}

      <Card title="Dokumenti">
        <div className="flex flex-wrap gap-3 align-items-center mb-3">
          <Dropdown
            value={filterType}
            options={docTypes.map((t) => ({ label: t.name, value: t.name }))}
            onChange={(e) => setFilterType(e.value || '')}
            placeholder="Registrator"
            appendTo="self"
            showClear
            style={{ minWidth: 220 }}
          />

          <Calendar
            value={filterDate}
            onChange={(e) => setFilterDate(e.value as Date)}
            dateFormat="dd.mm.yy"
            placeholder="Datum prijema"
            showIcon
          />

          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pretraži dokumente..."
              style={{ width: 280 }}
            />
          </span>

          <Button
            icon="pi pi-filter-slash"
            className="p-button-outlined"
            onClick={() => {
              setFilterType('');
              setFilterDate(null);
              setSearch('');
            }}
            aria-label="Reset filtera"
          />
        </div>

        {error && (
          <div className="mb-3">
            <Message severity="error" text={error} />
          </div>
        )}

        <DataTable
          value={filteredDocs}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
          responsiveLayout="scroll"
          emptyMessage="Nema dokumenata"
        >
          <Column header="Osnovni broj" body={baseNumberTemplate} sortable sortField="baseNumber" />
          <Column header="Podbroj" body={subNumberTemplate} sortable sortField="subNumber" style={{ width: '110px' }} />
          <Column header="Datum prijema" body={dateTemplate} sortable sortField="dateReceived" />
          <Column header="Predmet" body={subjectTemplate} style={{ width: '90px' }} />
          <Column field="sender" header="Pošiljalac" />
          <Column field="numberAndDate" header="Broj i datum" />
          <Column field="organizationalUnit" header="Organizaciona jedinica" />
          <Column field="oznaka" header="Oznaka" />
          <Column header="Prilozi" body={attachmentsTemplate} style={{ width: '110px' }} />
          <Column header="Akcije" body={actionsTemplate} style={{ width: '140px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={editingDoc ? 'Izmeni dokument' : 'Novi dokument'}
        visible={dialogVisible}
        style={{ width: '900px', maxWidth: '98vw' }}
        onHide={() => setDialogVisible(false)}
        footer={dialogFooter}
        modal
        draggable={false}
      >
        {error && (
          <div className="mb-3">
            <Message severity="error" text={error} />
          </div>
        )}

        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-6">
            <label htmlFor="doc-base">Osnovni broj *</label>
            <InputText
              id="doc-base"
              value={formData.baseNumber}
              onChange={(e) => setFormData((p) => ({ ...p, baseNumber: e.target.value }))}
              placeholder="npr. 335"
            />
          </div>
          <div className="field col-12 md:col-6">
            <label htmlFor="doc-sub">Podbroj</label>
            <InputText
              id="doc-sub"
              value={formData.subNumber}
              onChange={(e) => setFormData((p) => ({ ...p, subNumber: e.target.value }))}
              placeholder="npr. 335/1"
            />
          </div>

          <div className="field col-12">
            <label htmlFor="doc-subject">Predmet *</label>
            <InputText
              id="doc-subject"
              value={formData.subject}
              onChange={(e) => setFormData((p) => ({ ...p, subject: e.target.value }))}
            />
          </div>

          <div className="field col-12 md:col-6">
            <label htmlFor="doc-type">Registrator</label>
            <Dropdown
              id="doc-type"
              value={formData.documentType}
              options={docTypes.map((t) => ({ label: t.name, value: t.name }))}
              onChange={(e) => setFormData((p) => ({ ...p, documentType: e.value || '' }))}
              placeholder="Izaberite registrator"
              appendTo="self"
              showClear
              filter
            />
          </div>

          <div className="field col-12 md:col-6">
            <label htmlFor="doc-date">Datum prijema *</label>
            <Calendar
              id="doc-date"
              value={formData.dateReceived}
              onChange={(e) => setFormData((p) => ({ ...p, dateReceived: e.value as Date }))}
              dateFormat="dd.mm.yy"
              showIcon
            />
          </div>

          <div className="field col-12 md:col-6">
            <label htmlFor="doc-sender">Pošiljalac</label>
            <InputText
              id="doc-sender"
              value={formData.sender}
              onChange={(e) => setFormData((p) => ({ ...p, sender: e.target.value }))}
              placeholder="Prezime i ime / Naziv i mesto"
            />
          </div>
          <div className="field col-12 md:col-6">
            <label htmlFor="doc-number">Broj i datum</label>
            <InputText
              id="doc-number"
              value={formData.numberAndDate}
              onChange={(e) => setFormData((p) => ({ ...p, numberAndDate: e.target.value }))}
            />
          </div>

          <div className="field col-12 md:col-6">
            <label htmlFor="doc-org">Organizaciona jedinica</label>
            <InputText
              id="doc-org"
              value={formData.organizationalUnit}
              onChange={(e) => setFormData((p) => ({ ...p, organizationalUnit: e.target.value }))}
            />
          </div>
          <div className="field col-12 md:col-6">
            <label htmlFor="doc-oznaka">Oznaka</label>
            <InputText
              id="doc-oznaka"
              value={formData.oznaka}
              onChange={(e) => setFormData((p) => ({ ...p, oznaka: e.target.value }))}
            />
          </div>

          <div className="field col-12">
            <label htmlFor="doc-desc">Opis</label>
            <InputText
              id="doc-desc"
              value={formData.description}
              onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
            />
          </div>

          <div className="field col-12">
            <label>Prilozi</label>
            <div
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: '1rem',
                background: '#f9fafb'
              }}
            >
              <Button
                type="button"
                label="Odaberi prilog(e) (posle kreiranja)"
                icon="pi pi-plus"
                className="p-button-info"
                onClick={() => fileInputRef.current?.click()}
              />
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setPendingFiles(files);
                }}
              />
              <div style={{ marginTop: '0.75rem', color: '#6b7280', fontSize: 13 }}>
                Možete izabrati više fajlova. Ako dokument još nije kreiran, fajlovi će biti uploadovani odmah nakon klika na "Kreiraj".
              </div>
              {pendingFiles.length > 0 && (
                <div style={{ marginTop: '0.75rem' }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Odabrani fajlovi:</div>
                  <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                    {pendingFiles.map((f) => (
                      <li key={f.name}>{f.name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        header="Detalji dokumenta"
        visible={detailVisible}
        style={{ width: '700px', maxWidth: '95vw' }}
        onHide={() => setDetailVisible(false)}
        modal
      >
        {detailDoc && (
          <div className="p-fluid formgrid grid">
            <div className="col-12" style={{ fontWeight: 800, fontSize: 16 }}>
              {detailDoc.documentNumber}
            </div>
            <div className="col-12 md:col-6">
              <div style={{ color: '#6b7280' }}>Datum prijema</div>
              <div style={{ fontWeight: 600 }}>{formatDate(detailDoc.dateReceived || detailDoc.date)}</div>
            </div>
            <div className="col-12 md:col-6">
              <div style={{ color: '#6b7280' }}>Registrator</div>
              <div style={{ fontWeight: 600 }}>{detailDoc.documentType || '-'}</div>
            </div>
            <div className="col-12">
              <div style={{ color: '#6b7280' }}>Predmet</div>
              <div style={{ fontWeight: 600 }}>{detailDoc.subject}</div>
            </div>
            <div className="col-12 md:col-6">
              <div style={{ color: '#6b7280' }}>Pošiljalac</div>
              <div style={{ fontWeight: 600 }}>{detailDoc.sender || '-'}</div>
            </div>
            <div className="col-12 md:col-6">
              <div style={{ color: '#6b7280' }}>Broj i datum</div>
              <div style={{ fontWeight: 600 }}>{detailDoc.numberAndDate || detailDoc.referenceNumber || '-'}</div>
            </div>
            <div className="col-12 md:col-6">
              <div style={{ color: '#6b7280' }}>Organizaciona jedinica</div>
              <div style={{ fontWeight: 600 }}>{detailDoc.organizationalUnit || '-'}</div>
            </div>
            <div className="col-12 md:col-6">
              <div style={{ color: '#6b7280' }}>Oznaka</div>
              <div style={{ fontWeight: 600 }}>{detailDoc.oznaka || '-'}</div>
            </div>
            <div className="col-12">
              <div style={{ color: '#6b7280' }}>Opis</div>
              <div style={{ fontWeight: 600 }}>{detailDoc.description || '-'}</div>
            </div>
            <div className="col-12">
              <div style={{ color: '#6b7280' }}>Prilozi</div>
              {detailDoc.attachments && detailDoc.attachments.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                  {detailDoc.attachments.map((a) => (
                    <li key={a}>
                      <a href={financeDocumentsAPI.getAttachmentUrl(detailDoc._id, a)} target="_blank" rel="noreferrer">
                        {a}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ fontWeight: 600 }}>-</div>
              )}
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        header="Predmet"
        visible={subjectVisible}
        style={{ width: '600px', maxWidth: '95vw' }}
        onHide={() => setSubjectVisible(false)}
        modal
      >
        <div style={{ paddingTop: '0.25rem', fontSize: 15 }}>{subjectText}</div>
      </Dialog>

      <Dialog
        header={attachmentsDoc?.subject || 'Prilozi'}
        visible={attachmentsVisible}
        style={{ width: '700px', maxWidth: '95vw' }}
        onHide={() => setAttachmentsVisible(false)}
        modal
      >
        {attachmentsDoc?.attachments && attachmentsDoc.attachments.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {attachmentsDoc.attachments.map((a) => (
              <div
                key={a}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  padding: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '1rem'
                }}
              >
                <div className="flex align-items-center gap-2" style={{ minWidth: 0 }}>
                  <i className={isImage(a) ? 'pi pi-image' : (isPdf(a) ? 'pi pi-file-pdf' : 'pi pi-file')} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a}</span>
                </div>
                <div className="flex align-items-center gap-2">
                  <Button
                    icon="pi pi-eye"
                    className="p-button-outlined p-button-sm"
                    onClick={() => openPreview(attachmentsDoc, a)}
                    aria-label="Pregled"
                  />
                  <a
                    href={financeDocumentsAPI.getAttachmentUrl(attachmentsDoc._id, a)}
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: 'none' }}
                  >
                    <Button icon="pi pi-download" className="p-button-text p-button-sm" aria-label="Download" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div>-</div>
        )}
      </Dialog>

      <Dialog
        header={
          <div className="flex justify-content-between align-items-center" style={{ width: '100%' }}>
            <span>{previewName || 'Pregled priloga'}</span>
            <Button label="Štampaj" icon="pi pi-print" className="p-button-success p-button-sm" onClick={handlePrintPreview} />
          </div>
        }
        visible={previewVisible}
        style={{ width: '90vw', maxWidth: '1100px' }}
        onHide={() => setPreviewVisible(false)}
        modal
        draggable={false}
      >
        {previewUrl ? (
          <div style={{ height: '75vh' }}>
            {isImage(previewName) ? (
              <img src={previewUrl} alt={previewName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <iframe title={previewName} src={previewUrl} style={{ width: '100%', height: '100%', border: 'none' }} />
            )}
          </div>
        ) : (
          <div>-</div>
        )}
      </Dialog>
    </div>
  );
};

export default FinanceBookDocuments;


